<template>
  <Dialog :open="open" @update:open="handleClose" title="Create Custom Tool" size="lg">
    <form @submit.prevent="handleSubmit" class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Name
        </label>
        <Input v-model="form.name" placeholder="my-tool" class="w-full" :error="!!errors.name" />
        <p v-if="errors.name" class="mt-1 text-xs text-red-600 dark:text-red-400">
          {{ errors.name }}
        </p>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description
        </label>
        <Textarea v-model="form.description" :rows="2" placeholder="What this tool does" class="w-full" />
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Language
        </label>
        <select
          v-model="form.language"
          class="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
        >
          <option value="rust">Rust</option>
          <option value="typescript">TypeScript</option>
          <option value="python">Python</option>
        </select>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Source Code
        </label>
        <Textarea
          v-model="form.source"
          :rows="10"
          placeholder="Paste your tool source code..."
          class="w-full font-mono text-xs"
          :error="!!errors.source"
        />
        <p v-if="errors.source" class="mt-1 text-xs text-red-600 dark:text-red-400">
          {{ errors.source }}
        </p>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Tags (comma-separated)
        </label>
        <Input v-model="tagsInput" placeholder="tag1, tag2" class="w-full" />
      </div>

      <div class="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" @click="handleClose">Cancel</Button>
        <Button type="submit" variant="primary" :disabled="isSubmitting">
          {{ isSubmitting ? 'Building...' : 'Build & Deploy' }}
        </Button>
      </div>
    </form>
  </Dialog>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { Button, Dialog, Input, Textarea } from '@asabdev/asab-ui';
  import type { BuildFromSourcePayload, SourceLanguage } from '@/lib/mcp/types';

  interface Props {
    open: boolean;
  }

  interface Emits {
    (e: 'update:open', value: boolean): void;
    (e: 'submit', payload: BuildFromSourcePayload): void;
  }

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  const form = ref({
    name: '',
    description: '',
    language: 'typescript' as SourceLanguage,
    source: '',
  });
  const tagsInput = ref('');
  const errors = ref<Record<string, string>>({});
  const isSubmitting = ref(false);

  watch(
    () => props.open,
    (open) => {
      if (open) {
        form.value = { name: '', description: '', language: 'typescript', source: '' };
        tagsInput.value = '';
        errors.value = {};
      }
    },
  );

  const handleClose = () => emit('update:open', false);

  const validate = (): boolean => {
    errors.value = {};
    if (!form.value.name.trim()) errors.value.name = 'Name is required';
    if (!form.value.source.trim()) errors.value.source = 'Source code is required';
    return Object.keys(errors.value).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    isSubmitting.value = true;
    const tags = tagsInput.value
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    emit('submit', {
      name: form.value.name.trim(),
      description: form.value.description.trim(),
      language: form.value.language,
      source: form.value.source,
      tags: tags.length > 0 ? tags : undefined,
    });
    isSubmitting.value = false;
    handleClose();
  };
</script>
